import { createAuthenticatedGraphQLClient } from '../../auth/createAuthenticatedGraphQLClient';
import { resolveClientEnv } from '../../graphql/config/resolveClientEnv';
import type { VoterDetailQuery, VoterDetailQueryVariables } from '../../graphql/queries/voterDetail.generated';
import type { VoterDoorVisitsQuery, VoterDoorVisitsQueryVariables } from '../../graphql/queries/voterDoorVisits.generated';
import type { VoterListQuery, VoterListQueryVariables } from '../../graphql/queries/voterList.generated';
import type { VoterRegistrationStatesQuery } from '../../graphql/queries/voterRegistrationStates.generated';
import { GraphQLRequestError } from '../../graphql/routing/apiRouting';
import {
  VOTER_DETAIL_DOCUMENT,
  VOTER_DOOR_VISITS_DOCUMENT,
  VOTER_LIST_DOCUMENT,
  VOTER_REGISTRATION_STATES_DOCUMENT,
} from './voterDocuments';

export type VoterFetchImpl = NonNullable<Parameters<typeof createAuthenticatedGraphQLClient>[0]['fetchImpl']>;

export const VOTER_LIST_ERROR_FALLBACK = "Couldn't load voters. Please try again.";
export const VOTER_STATES_ERROR_FALLBACK = "Couldn't load registration states. Please try again.";
export const VOTER_DETAIL_ERROR_FALLBACK = "Couldn't load this voter. Please try again.";
export const VOTER_VISITS_ERROR_FALLBACK = "Couldn't load associated door visits. Please try again.";

type VoterRequestOptions = {
  signal: AbortSignal;
  fetchImpl?: VoterFetchImpl;
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

export const isAbortError = (error: unknown): boolean => isRecord(error) && Reflect.get(error, 'name') === 'AbortError';

const graphQLErrorMessage = (error: GraphQLRequestError): string | null => {
  for (const entry of error.graphQLErrors ?? []) {
    if (!isRecord(entry)) continue;
    const message = Reflect.get(entry, 'message');
    if (typeof message === 'string' && message.trim().length > 0) return message.trim();
  }

  return null;
};

// Surface the server message when GraphQL rejected the input. Never rewrite or truncate it.
export const toVoterRequestErrorMessage = (error: unknown, fallback: string): string => {
  if (isAbortError(error)) return fallback;
  if (error instanceof GraphQLRequestError) return graphQLErrorMessage(error) ?? fallback;
  if (error instanceof Error && error.message.trim().length > 0) return error.message;
  return fallback;
};

const request = async <TData>(
  document: Parameters<ReturnType<typeof createAuthenticatedGraphQLClient>['request']>[0]['document'],
  variables: VoterListQueryVariables | VoterDetailQueryVariables | VoterDoorVisitsQueryVariables | Record<string, never>,
  operationName: string,
  options: VoterRequestOptions,
): Promise<TData> => {
  const client = createAuthenticatedGraphQLClient({ env: resolveClientEnv(), fetchImpl: options.fetchImpl });
  return client.request<TData>({ document, variables, operationName, signal: options.signal });
};

export const fetchVoterList = async ({
  search,
  state,
  first,
  offset,
  signal,
  fetchImpl,
}: VoterRequestOptions & {
  search?: string;
  state?: string;
  first: number;
  offset: number;
}): Promise<VoterListQuery['voterList']> => {
  const variables: VoterListQueryVariables = {
    first,
    offset,
    ...(search === undefined ? {} : { search }),
    ...(state === undefined ? {} : { state }),
  };
  const data = await request<VoterListQuery>(VOTER_LIST_DOCUMENT, variables, 'VoterList', { signal, fetchImpl });
  return data.voterList;
};

export const fetchVoterRegistrationStates = async ({
  signal,
  fetchImpl,
}: VoterRequestOptions): Promise<string[]> => {
  const data = await request<VoterRegistrationStatesQuery>(VOTER_REGISTRATION_STATES_DOCUMENT, {}, 'VoterRegistrationStates', {
    signal,
    fetchImpl,
  });
  if (!Array.isArray(data.voterRegistrationStates) || data.voterRegistrationStates.some(state => typeof state !== 'string')) {
    throw new Error('Voter registration states response was incomplete.');
  }
  return data.voterRegistrationStates;
};

export const fetchVoterDetail = async ({
  id,
  signal,
  fetchImpl,
}: VoterRequestOptions & { id: string }): Promise<VoterDetailQuery['voterDetail']> => {
  const variables: VoterDetailQueryVariables = { id };
  const data = await request<VoterDetailQuery>(VOTER_DETAIL_DOCUMENT, variables, 'VoterDetail', { signal, fetchImpl });
  return data.voterDetail;
};

export const fetchVoterDoorVisits = async ({
  id,
  first,
  offset,
  signal,
  fetchImpl,
}: VoterRequestOptions & {
  id: string;
  first: number;
  offset: number;
}): Promise<NonNullable<VoterDoorVisitsQuery['voterDoorVisits']>> => {
  const variables: VoterDoorVisitsQueryVariables = { id, first, offset };
  const data = await request<VoterDoorVisitsQuery>(VOTER_DOOR_VISITS_DOCUMENT, variables, 'VoterDoorVisits', {
    signal,
    fetchImpl,
  });
  return data.voterDoorVisits ?? { __typename: 'VoterDoorVisitsResult', total: 0, items: [] };
};
